import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProfileCustomizationForm = ({ formData, errors, onChange }) => {
  const handleLogoUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onChange('logo', reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleCulturePhotoUpload = (e) => {
    const files = Array.from(e?.target?.files || []);
    const readers = files?.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader?.result);
        reader.readAsDataURL(file);
      });
    });
    Promise.all(readers)?.then(results => {
      onChange('culturePhotos', [...(formData?.culturePhotos || []), ...results]);
    });
  };

  const handleRemoveCulturePhoto = (index) => {
    const updated = formData?.culturePhotos?.filter((_, i) => i !== index);
    onChange('culturePhotos', updated);
  };

  const brandColorPresets = [
    '#FF6B35', '#004E89', '#1A936F', '#F77F00', '#9B59B6',
    '#E74C3C', '#3498DB', '#2ECC71', '#F39C12', '#34495E'
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Company Logo</h3>
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="w-32 h-32 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted overflow-hidden">
            {formData?.logo ? (
              <img src={formData?.logo} alt="Company logo" className="w-full h-full object-cover" />
            ) : (
              <Icon name="Building2" size={48} color="var(--color-muted-foreground)" />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm text-muted-foreground mb-3">
              Upload your company logo. Recommended size: 400x400px. Formats: JPG, PNG
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
              id="logo-upload"
            />
            <label htmlFor="logo-upload">
              <Button variant="outline" size="sm" asChild>
                <span>
                  <Icon name="Upload" size={16} className="mr-2" />
                  Upload Logo
                </span>
              </Button>
            </label>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Brand Color</h3>
        <div className="flex flex-wrap gap-3 mb-4">
          {brandColorPresets?.map((color) => (
            <button
              key={color}
              onClick={() => onChange('brandColor', color)}
              className={`w-12 h-12 rounded-lg border-2 transition-smooth ${
                formData?.brandColor === color ? 'border-foreground scale-110' : 'border-border'
              }`}
              style={{ backgroundColor: color }}
              aria-label={`Select color ${color}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="color"
            value={formData?.brandColor}
            onChange={(e) => onChange('brandColor', e?.target?.value)}
            className="w-20 h-10"
          />
          <Input
            type="text"
            value={formData?.brandColor}
            onChange={(e) => onChange('brandColor', e?.target?.value)}
            placeholder="#FF6B35"
            className="flex-1"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Company Culture Showcase</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {formData?.culturePhotos?.map((photo, index) => (
            <div key={index} className="relative group">
              <img
                src={photo}
                alt={`Culture photo ${index + 1}`}
                className="w-full h-32 object-cover rounded-lg"
              />
              <button
                onClick={() => handleRemoveCulturePhoto(index)}
                className="absolute top-2 right-2 w-6 h-6 bg-error text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth"
                aria-label="Remove photo"
              >
                <Icon name="X" size={14} />
              </button>
            </div>
          ))}
          {formData?.culturePhotos?.length < 8 && (
            <label
              htmlFor="culture-photos"
              className="w-full h-32 border-2 border-dashed border-border rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-muted transition-smooth"
            >
              <Icon name="Plus" size={24} color="var(--color-muted-foreground)" />
              <span className="text-xs text-muted-foreground mt-1">Add Photo</span>
            </label>
          )}
        </div>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleCulturePhotoUpload}
          className="hidden"
          id="culture-photos"
        />
        <p className="text-sm text-muted-foreground">Upload up to 8 photos showcasing your workplace, team, and culture</p>
      </div>

      <div>
        <Input
          label="Company Video URL"
          type="url"
          placeholder="https://www.youtube.com/watch?v=..."
          value={formData?.videoUrl}
          onChange={(e) => onChange('videoUrl', e?.target?.value)}
          description="Add a YouTube or Vimeo link to showcase your company"
        />
      </div>
    </div>
  );
};

export default ProfileCustomizationForm;